/* eslint-disable implicit-arrow-linebreak */
/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Snigdha <snigdha.dhanireddy@mroads.com>, August 2020
 * @format
 * @flow
 */

import axios from "axios";
import URLs from "./configuration";
import UTIL from '../util'

// TODO: need to recheck on timeout in future.
axios.defaults.timeout = 20000;
axios.defaults.headers.common["Content-Type"] = "application/json";

axios.interceptors.request.use(
  config => {
    const newConfig = { ...config };
    newConfig.metadata = { startTime: new Date().getTime() };
    return newConfig;
  }, error => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  res => {
    const newRes = { ...res };
    newRes.config.metadata.endTime = new Date().getTime();
    newRes.responseTime = newRes.config.metadata.endTime - newRes.config.metadata.startTime;
    return newRes;
  }, error => {
    let newError = { ...error };
    newError.config.metadata.endTime = new Date().getTime();
    newError.responseTime = newError.config.metadata.endTime - newError.config.metadata.startTime;
    if (newError.response)
      newError.response.responseTime = newError.config.metadata.endTime - newError.config.metadata.startTime;
    return Promise.reject(newError);
  }
);

export default class API {
  static setURL(url: string) {
    axios.defaults.baseURL = url;
  }

  static getURL() {
    return axios.defaults.baseURL;
  }

  static setApiHeaders(apiKey: string, username: string, password: string) {
    axios.defaults.headers.common["x-api-key"] = apiKey;
    axios.defaults.headers.common["x-api-authentication"] = `${username}:${password}`;
  }

  static setIdsInHeaders(storeId: any, deviceId: string, employeeId: any) {
    axios.defaults.headers.common["store-id"] = storeId || null;
    axios.defaults.headers.common["device-id"] = deviceId || null;
    axios.defaults.headers.common["employee-id"] = employeeId || null;
  }

  static getStoreInfo = (deviceId: string) =>
    axios(URLs.getStoreInfo + deviceId);

  static activateDevice = (payload: {
    latitude: string,
    longitude: string,
    activationCode: string,
    isSimPay: Boolean,
  }) => axios.post(URLs.activateDevice, payload);

  static reactivateDevice = (payload) => axios.post(URLs.reactivateDevice, payload);

  static updateSyncStatus = (body) => axios.post(URLs.updateSyncStatus, body);

  static getCheckForFireboard = (payload: Object) =>
    axios.post(`${URLs.getCheckForFireboard}`, payload);

  static getFutureOrdersList = payload => axios.post(URLs.futureOrdersList, payload);

  static getFutureOrders = payload => axios.post(URLs.getFutureOrders, payload);

  static disableOrders = body => axios.post(URLs.disableOrders, body);

  static getMemberDetails = ({ memberId }) =>
    axios.get(URLs.getMemberDetails.replace('{memberId}', memberId));

  static updatePrepTime = body => axios.post(URLs.updatePrepTime, body);

  static updateProdTime = body => axios.post(URLs.updateProdTime, body);

  static payNow = body => axios.post(URLs.payNow, body);

  static checkInRequest = body => axios.post(URLs.checkInRequest, body);

  static generateTextToPay = body => axios.post(URLs.generateTextToPay, body);

  static acknowledgeIdCheck = body => axios.post(URLs.acknowledgeIdCheck, body);

  static acknowledgeOverServe = body => axios.post(URLs.acknowledgeOverServe, body);

  static pingURL = (url: string, options: Object = {}) => axios.get(url, options);

  static pingOneDine = deviceExternalId => axios.get(URLs.pingOneDine + deviceExternalId);

  static reactivateDevice = payload => axios.post(URLs.reactivateDevice, payload);

  static searchItemStock = payload => axios.post(URLs.searchItemStock, payload);

  static searchModifierStock = payload => axios.post(URLs.searchModifierStock, payload);

  static getOutOfStockItemList = payload => axios.post(URLs.getOutOfStockItemList, payload);

  static getOutOfStockModifierList = payload => axios.post(URLs.getOutOfStockModifierList, payload);

  static updateItemStock = payload => axios.post(URLs.updateItemStock, payload);

  static updateModifierStock = payload => axios.post(URLs.updateModifierStock, payload);

  static acknowledgeNotification = acknowledgeNotificationData =>
    axios.post(URLs.acknowledgeNotification, acknowledgeNotificationData);

  static reservationAPI = ({ type, data: payload, method = 'get' }) => {
    return axios[method](`${URLs.reservationAPI}${type}`, payload);
  }

  static deleteWaitlist = ({ type, data: payload, method = 'get' }) => {
    return axios.get(`${URLs.reservationAPI}${type}/${payload.bookingId}`);
  }

  static waitlistReservationAPI = ({ type, data: payload }) =>
    axios.post(`${URLs.reservationAPI}${type}`, payload);

  static login = (payload, hasLocation = false) => {
    if (hasLocation && !payload.hasOwnProperty('geoLocation')) {
      const location = UTIL.location;
      if (location) {
        const { latitude, longitude } = location;
        payload = {
          ...payload,
          longitude,
          latitude,
          geoLocation: `${latitude},${longitude}`,
        };
      }
    }
    return axios.post(URLs.login, payload);
  }

  static pairWatch = payload => axios.post(URLs.pairWatch, payload);

  static seatTable = payload => axios.post(URLs.seatTable, payload);

  static orderStatusUpdate = payload => axios.post(URLs.orderStatusUpdate, payload);

  static tableReceiptPrint = (payload: TableReceiptPrint) =>
    axios.post(URLs.tableReceiptPrint, payload);

  static fireboardSendSMS = body => axios.post(URLs.fireboardSendSMS, body);

  static orderStatusPaid = payload => axios.post(URLs.orderStatusPaid, payload);

  static syncCheck = payload => axios.post(URLs.syncCheck + payload.checkHeaderId);

  static printItems = body => axios.post(URLs.printItems, body);

  static printCheck = body => axios.post(URLs.printCheck, body);

  static preAuthCompleteFB = payload => axios.post(URLs.preAuthCompleteFB, payload);

  static getStationGroup = storeId => axios.get(URLs.stationGroup + storeId);

  static getStoreLayout = storeId => axios.get(URLs.storeLayout + storeId);

  static updateStoreLayout = payload => axios.post(URLs.updateStoreLayout, payload);
  static printOrderTicket = payload => axios.post(URLs.getPrintOrderTicket, payload);
  static getVenueInfo = dId => axios.get(URLs.getVenueInfo + dId);

  static sensorCode = payload => axios.post(URLs.sensorCode, payload);

  static initializeCashPayment = payload => axios.post(URLs.cashPayment, payload);

  static initializePayment = payload => axios.post(URLs.initializePayment, payload);

  static paymentIngenicoComplete = payload => axios.post(URLs.paymentIngenicoComplete, payload);

  static validateGiftCard = payload => axios.post(URLs.validateGiftCard, payload);

  static paymentGiftCardInit = payload => axios.post(URLs.paymentGiftCardInit, payload);

  static paymentGiftCardComplete = payload => axios.post(URLs.paymentGiftCardComplete, payload);

  static getStoreJobs = ({ storeId }) => axios.get(URLs.getStoreJobs + storeId);

  static emailReceipt = payload => axios.post(URLs.emailReceipt, payload);

  static smsReceipt = payload => axios.post(URLs.smsReceipt, payload);

  static receiptPrint = payload => axios.post(URLs.receiptPrint, payload);

  static getTips = payload => axios.post(URLs.getTips, payload);

  static getMergedTables = payload => axios.post(URLs.getMergedTables, payload);

  static mergeTable = payload => axios.post(URLs.mergeTable, payload);

  static unMergeTable = payload => axios.post(URLs.unMergeTable, payload);

  static getStationEmployees = payload => axios.post(URLs.getStationEmployees, payload);

  static assignEmployee = payload => axios.post(URLs.assignEmployee, payload);

  static removeEmployeeAssignment = payload => axios.post(URLs.removeEmployeeAssignment, payload);

  static assignTable = payload => axios.post(URLs.assignTable, payload);

  static removeTableAssignment = payload => axios.post(URLs.removeTableAssignment, payload);

  static retryPOSPayment = payload => axios.post(URLs.retryPOSPayment, payload);

  static getCustomSmsTemplates = storeId => axios.get(URLs.customSmsTemplates + storeId);

  static getSensors = storeId => axios.get(URLs.sensors + storeId);

  static updateSensorGroup = payload => axios.post(URLs.updateSensorGroup, payload);

  static updateVenueOrder = body => axios.post(URLs.updateVenueOrder, body);

  static updateFoodRunner = body => axios.post(URLs.updateFoodRunner, body);

  static deRegisterSensor = sensorId => axios.post(URLs.deRegisterSensor + sensorId, null);

  static registerSensor = body => axios.post(URLs.registerSensor, body);

  static getNamedSensors = storeId => axios.get(URLs.namedSensor + storeId);

  static voucherCheckIn = body => axios.post(URLs.voucherCheckIn, body);

  static voucherSearch = body => axios.post(URLs.voucherSearch, body);

  static getStoreProductions = storeId => axios.get(URLs.storeProductions + storeId);

  static overrideStoreProduction = body => axios.post(URLs.storeProductionOverride, body);

  static getProductionOverrideReasons = () => axios.get(URLs.productionOverrideReasons);

  static cubbyAssign = body => axios.post(URLs.cubbyAssign, body);

  static orderPickedUp = body => axios.post(URLs.orderPickedUp, body);

  static textToGetLocation = checkHeaderId => axios.post(URLs.textToGetLocation + checkHeaderId, null);

  static gpsLocationPickedUp = body => axios.post(URLs.gpsLocationPickedUp, body);

  static updateGuestInformation = body => axios.post(URLs.updateGuestInformation, body);

  static getStoreChecks = storeId => axios.get(URLs.getChecks + storeId);

  static getVenueChecks = venueId => axios.get(URLs.getVenueChecks + venueId);

  static getStoreNotifications = storeId => axios.get(URLs.getNotifications + storeId);

  static getStoreBookings = storeId => axios.get(URLs.getBookings + storeId);

  static getSectionEmployee = payload => axios.post(URLs.getSectionEmployee, payload);

  static assignSectionToEmployee = payload => axios.post(URLs.assignSectionToEmployee, payload);

  static assignEmployeeToSection = payload => axios.post(URLs.assignEmployeeToSection, payload);

  static unAssignSectionFromEmployee = payload => axios.post(URLs.unAssignSectionFromEmployee, payload);

  static unAssignEmployeeFromSection = payload => axios.post(URLs.unAssignEmployeeFromSection, payload);

  static renameSeat = payload => axios.post(URLs.renameSeat, payload);

  static getStoreLayoutBySensorGroup = (storeId, sensorGroupId) => {
    let url = URLs.getStoreLayoutBySensorGroup;
    url = url.replace('{storeId}', storeId);
    url = url.replace('{sensorGroupId}', sensorGroupId);
    return axios.get(url);
  };

  static updateStoreLayoutBySensorGroup = payload => axios.post(URLs.updateStoreLayoutBySensorGroup, payload);

  static firePendingItems = payload => axios.post(URLs.firePendingItems, payload);

  static ageVerification = payload => axios.post(URLs.ageVerification, payload);
  static ageRestrictedItemsUpdateForCheck = payload => axios.post(URLs.ageRestrictedItemsUpdateForCheck, payload);
  static ageRestrictedItemsUpdateForSeat = payload => axios.post(URLs.ageRestrictedItemsUpdateForSeat, payload);

  static logToApiLog = (payload, path) => {
    path = path && path[0] === '/' ? path.substring(1) : path;
    const url = path ? `${URLs.logToApiLog}/${path}` : URLs.logToApiLog;
    return axios.post(url, payload);
  }

  static bookingClear = payload => axios.post(URLs.bookingClear, payload);

  static bookingCancel = payload => axios.post(URLs.bookingCancel, payload);

  static bookingCreate = payload => axios.post(URLs.bookingCreate, payload);

  static bookingUpdate = payload => axios.post(URLs.bookingUpdate, payload);

  static bookingComplete = payload => axios.post(URLs.bookingComplete, payload);

  static bookingMove = payload => axios.post(URLs.bookingMove, payload);

  static moveGuest = payload => axios.post(URLs.moveGuest, payload);

  static syncIfModified = payload => axios.post(URLs.syncIfModified, payload);

  static voidCheck = payload => axios.post(URLs.voidCheck, payload);
}
